import * as React from "react";
import { NodeStore } from "../../stores";
import { ORIGIN } from "../../Constants";

interface ResizableNodeProps<T extends NodeStore> {
    store: T;
}

/**
 * This class is a base view for nodes that supports resizing.
 * It encapsulates pointer driven drag resizing and updates the 
 * underlying store dimensions, while delegating visual rendering
 * to each subclass.
 */
export abstract class ResizableNodeView<
    T extends NodeStore
> extends React.Component<ResizableNodeProps<T>> {

    private isResizing = false;
    private startX = ORIGIN;
    private startY = ORIGIN;
    private startWidth = ORIGIN;
    private startHeight = ORIGIN;

    /**
     * This method begins resizing when the user interacts with the resize handle
     * by recording the initial pointer position and node dimensions.
     * @param event The pointer event that starts the resize action.
     */
    onResizePointerDown = (event: React.PointerEvent) => {
        event.stopPropagation();
        event.preventDefault();

        this.isResizing = true;
        document.body.classList.add("dragging-node");

        this.startX = event.clientX;
        this.startY = event.clientY;
        this.startWidth = this.props.store.width;
        this.startHeight = this.props.store.height;

        document.addEventListener("pointermove", this.onResizePointerMove);
        document.addEventListener("pointerup", this.onResizePointerUp);
    };

    /**
     * This method updates the nodes dimensions as the user drags the resize handle.
     * It calculates the change in pointer position and has a minimum size included.
     * @param event The pointer event that updates the node dimensions.
     */
    onResizePointerMove = (event: PointerEvent) => {
        if (!this.isResizing) return;

        const deltaX = event.clientX - this.startX;
        const deltaY = event.clientY - this.startY;
        this.props.store.width = Math.max(100, this.startWidth + deltaX);
        this.props.store.height = Math.max(100, this.startHeight + deltaY);
    };

    /**
     * This method ends the resizing action when the user releases the pointer.
     * @param event The pointer event that ends the resize action.
     */
    onResizePointerUp = () => {
        this.isResizing = false;
        document.body.classList.remove("dragging-node");
        document.removeEventListener("pointermove", this.onResizePointerMove);
        document.removeEventListener("pointerup", this.onResizePointerUp);
    };

    /**
     * This method provides an optional class name for the subclasses.
     * @returns 
     */
    protected getNodeClassName(): string {
        return "";
    }

    /**
     * This abstract method must be implemented by subclasses to render the node content.
     */
    protected abstract renderContent(): React.ReactNode;

    render() {
    const store = this.props.store;

    /**
     * This method renders the node with a resize handle, its content, and styles.
     */
    return (
        <div
            className={`node ${this.getNodeClassName()}`}
            style={store.style}
            onPointerDown={(e) => e.stopPropagation()}
        >
            {this.renderContent()}
            <div
                className="resize-handle"
                onPointerDown={this.onResizePointerDown}
            />
        </div>
    );
}
}